// ============================================================
// src/components/TodaysGigCard.js
//
// ── THEME SUPPORT ─────────────────────────────────────────────
// All hardcoded rgba/hex colour values that previously lived as
// inline `style` props have been moved to .tgc-* CSS classes in
// index.css section 30.  The CSS classes use CSS variables, so
// every theme (its-pink, green-gush, web, blue-shock) is
// respected automatically with no JS changes needed per-theme.
//
// Shared JS style objects (labelStyle, bigNumberStyle,
// infoBlockStyle) are also gone; replaced by:
//   .tgc-label        — ALL-CAPS muted section label
//   .tgc-big-number   — large display-font time/countdown
//   .tgc-block        — dark info panel
//
// ── ROUTING ORIGIN ────────────────────────────────────────────
// gigDate prop (passed from dashboard for both Today and Tomorrow
// sections) forces TodaysGigCard to call getActiveTripForDate()
// instead of activeTripToday, matching the calendar modal path.
// If stay_lat/stay_lng are null, calculateTravel geocodes
// activeTrip.stay_address via Nominatim as a fallback.
//
// ── GPS BANNER ────────────────────────────────────────────────
// gpsToHomeMinutes is reset to null whenever effectiveHomeLat/Lng
// change (e.g. when trip data loads after initial render) so the
// away-from-home banner never shows stale Charlotte coords while
// the trip stay address is the actual origin.
// Banner text says "from your stay" when a trip is active instead
// of "from home".
//
// ── STALE RUN CANCELLATION ───────────────────────────────────
// travelGenRef is a monotonically-increasing counter. Each reset
// effect increments it; calculateTravel captures the value at
// start and checks it before writing any state. If the counter
// changed while the async routing was running (because a trip
// loaded mid-flight and reset the origin), the old run aborts
// without overwriting the correct new results.
//
// ── PICKUP TIME ───────────────────────────────────────────────
// When gig.pickup_time is set the get-ready time is derived from
// pickup_time - prepMinutes. Routing still runs and is shown as
// "LATEST PICKUP BY" so the performer can verify their scheduled
// pickup arrives before that deadline. If the scheduled pickup is
// after the routing's latest pickup time a red warning appears.
// pickup_time can be edited inline on the card (saves to Supabase)
// or on the gig edit/add form.
//
// ── PICKUP NOTIFICATION RESCHEDULE ───────────────────────────
// savePickupTime and clearPickupTime update scheduled_notifications
// directly after saving pickup_time. Both get_ready and
// advance_get_ready rows are deleted and re-inserted with correct
// send_at times.
//
// ── HIDE EARNINGS ─────────────────────────────────────────────
// hideEarnings prop hides the Pay block.
//
// ── ROUTING FIXES ────────────────────────────────────────────
// FIX 1: buildMapsUrl uses venue name + address string (not coords).
// FIX 2: geocodeAddress accepts optional city + state params.
// FIX 3: calculateTravel always geocodes venue fresh from address.
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useMusicPlayer } from '../context/MusicPlayerContext'
import { calculateVenueToVenueLeaveTime } from '../lib/gigRouting'
import { useTripMode, getTransitBufferMinutes } from '../hooks/useTripMode'
import { supabase } from '../lib/supabaseClient'

// ── CONSTANTS ─────────────────────────────────────────────────
const TRAVEL_BUFFER_MINUTES       = 20
const AWAY_FROM_HOME_THRESHOLD_KM = 0.5
const HOME_ARRIVAL_BUFFER_MINUTES = 15

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org/search'
const MAPBOX_TOKEN   = process.env.REACT_APP_MAPBOX_TOKEN
const MAPBOX_BASE    = 'https://api.mapbox.com/directions/v5/mapbox/driving-traffic'


// ── HELPERS ───────────────────────────────────────────────────

function parseShowTime(timeStr, gigDate = null) {
  if (!timeStr) return null
  const t = timeStr.trim().toLowerCase()
  const makeDate = (hours, minutes) => {
    if (gigDate) {
      const [y, mo, d] = gigDate.split('-').map(Number)
      return new Date(y, mo - 1, d, hours, minutes, 0, 0)
    }
    const d = new Date()
    d.setHours(hours, minutes, 0, 0)
    return d
  }
  const withSeconds = t.match(/^(\d{1,2}):(\d{2}):\d{2}$/)
  if (withSeconds) return makeDate(parseInt(withSeconds[1], 10), parseInt(withSeconds[2], 10))
  const twelveHour = t.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/)
  if (twelveHour) {
    let h = parseInt(twelveHour[1], 10)
    const m = parseInt(twelveHour[2], 10)
    if (twelveHour[3] === 'am' && h === 12) h = 0
    if (twelveHour[3] === 'pm' && h !== 12) h += 12
    return makeDate(h, m)
  }
  const twentyFour = t.match(/^(\d{1,2}):(\d{2})$/)
  if (twentyFour) return makeDate(parseInt(twentyFour[1], 10), parseInt(twentyFour[2], 10))
  return null
}

function formatDuration(s) {
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h > 0 ? `${h} hr ${m} min` : `${m} min`
}

function formatTime(date) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

function formatTimeStr(timeStr) {
  if (!timeStr) return ''
  const [h, m] = timeStr.split(':').map(Number)
  const period = h >= 12 ? 'PM' : 'AM'
  const h12    = h % 12 || 12
  return `${h12}:${String(m).padStart(2, '0')} ${period}`
}

// FIX 1: Always use venue name + address string for navigation.
function buildMapsUrl(gig) {
  const query = [gig.venue_name, gig.venue_address].filter(Boolean).join(', ')
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)
  return isIOS
    ? `https://maps.apple.com/?q=${encodeURIComponent(query)}`
    : `https://maps.google.com/maps?q=${encodeURIComponent(query)}`
}

// FIX 2: Geocode via Nominatim with optional city/state hint.
async function geocodeAddress(addressStr, city = '', state = '') {
  const hint  = [city, state].filter(Boolean).join(', ')
  const query = hint ? `${addressStr}, ${hint}` : addressStr
  const clean = (query || '')
    .replace(/\b(apt|apartment|unit|ste|suite|#|no\.?)\s*[a-z0-9-]+/gi, '')
    .replace(/\s+[a-zA-Z]\s*,/g, ',')
    .replace(/\s{2,}/g, ' ')
    .trim()
  const res  = await fetch(
    `${NOMINATIM_BASE}?q=${encodeURIComponent(clean)}&format=json&limit=1`,
    { headers: { 'Accept-Language': 'en' } }
  )
  const data = await res.json()
  if (!data || !data.length) throw new Error(`Address not found: ${addressStr}`)
  return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) }
}

// Mapbox driving-traffic: returns drive time in seconds with live traffic.
async function fetchDriveSeconds(fromLon, fromLat, toLon, toLat) {
  const res  = await fetch(
    `${MAPBOX_BASE}/${fromLon},${fromLat};${toLon},${toLat}` +
    `?access_token=${MAPBOX_TOKEN}&geometries=geojson&overview=false`
  )
  const data = await res.json()
  if (!data.routes?.length) throw new Error('No driving route found')
  return data.routes[0].duration
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R    = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a    = Math.sin(dLat / 2) ** 2
              + Math.cos(lat1 * Math.PI / 180)
              * Math.cos(lat2 * Math.PI / 180)
              * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}


// ════════════════════════════════════════════════════════════════
// reschedulePickupNotification
// ════════════════════════════════════════════════════════════════
async function reschedulePickupNotification({ gigId, gigDate, newPickupTime, prepMinutes }) {
  try {
    const { data: { user: currentUser } } = await supabase.auth.getUser()
    if (!currentUser) return

    const { error: deleteError } = await supabase
      .from('scheduled_notifications')
      .delete()
      .eq('gig_id',   gigId)
      .eq('user_id',  currentUser.id)
      .in('notif_type', ['get_ready', 'advance_get_ready'])
      .eq('sent', false)

    if (deleteError) {
      console.error('[reschedulePickupNotification] delete error:', deleteError)
      return
    }

    if (newPickupTime && gigDate) {
      const [gigYear, gigMonth, gigDay] = gigDate.split('-').map(Number)
      const [pickupHour, pickupMin]     = newPickupTime.split(':').map(Number)

      const pickupDate   = new Date(gigYear, gigMonth - 1, gigDay, pickupHour, pickupMin, 0, 0)
      const getReadyDate = new Date(pickupDate.getTime() - prepMinutes * 60 * 1000)
      const now          = new Date()

      const { data: prefs } = await supabase
        .from('notification_preferences')
        .select('advance_notice_minutes')
        .eq('user_id', currentUser.id)
        .single()

      const advanceMinutes = prefs?.advance_notice_minutes || 0
      const advanceDate    = new Date(getReadyDate.getTime() - advanceMinutes * 60 * 1000)

      const rowsToInsert = []

      if (getReadyDate > now) {
        rowsToInsert.push({
          user_id:    currentUser.id,
          gig_id:     gigId,
          notif_type: 'get_ready',
          send_at:    getReadyDate.toISOString(),
          sent:       false,
        })
      }

      if (advanceMinutes > 0 && advanceDate > now) {
        rowsToInsert.push({
          user_id:    currentUser.id,
          gig_id:     gigId,
          notif_type: 'advance_get_ready',
          send_at:    advanceDate.toISOString(),
          sent:       false,
        })
      }

      if (rowsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('scheduled_notifications')
          .insert(rowsToInsert)

        if (insertError) {
          console.error('[reschedulePickupNotification] insert error:', insertError)
        }
      }
    }
  } catch (err) {
    console.error('[reschedulePickupNotification] unexpected error:', err)
  }
}


// ════════════════════════════════════════════════════════════════
// useLeaveForHomeNotification
// ════════════════════════════════════════════════════════════════
function useLeaveForHomeNotification({ gig, gpsToHomeMinutes, getReadyTime, showHomeRouting, isTrip }) {
  const notifiedRef  = useRef(false)
  const timeoutIdRef = useRef(null)

  useEffect(() => {
    if (timeoutIdRef.current) { clearTimeout(timeoutIdRef.current); timeoutIdRef.current = null }
    if (!showHomeRouting || gpsToHomeMinutes === null || !getReadyTime || notifiedRef.current) return
    if (!('Notification' in window) || Notification.permission !== 'granted') return

    const leaveAt     = new Date(getReadyTime.getTime() - gpsToHomeMinutes * 60 * 1000)
    const msUntil     = leaveAt.getTime() - Date.now()
    if (msUntil < -5 * 60 * 1000) return

    const delay       = Math.max(0, Math.min(msUntil, 4 * 60 * 60 * 1000))
    const gigName     = gig.name || gig.venue_name || 'your gig'
    const leaveLabel  = formatTime(leaveAt)
    const destination = isTrip ? 'your trip home' : 'home'
    const title       = isTrip ? 'Head back to your trip home' : 'Head home now'

    timeoutIdRef.current = setTimeout(() => {
      if (Notification.permission !== 'granted' || notifiedRef.current) return
      notifiedRef.current = true
      try {
        const body = `Leave by ${leaveLabel} to get back to ${destination} and have time to chill before getting ready for ${gigName}.`
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.ready
            .then(r => r.showNotification(title, { body, icon: '/logo192.png', tag: `leave-for-home-${gig.id}` }))
            .catch(() => new Notification(title, { body, tag: `leave-for-home-${gig.id}` }))
        } else {
          new Notification(title, { body, tag: `leave-for-home-${gig.id}` })
        }
      } catch (err) { console.error('[LeaveHomeNotif]', err) }
    }, delay)

    return () => { if (timeoutIdRef.current) { clearTimeout(timeoutIdRef.current); timeoutIdRef.current = null } }
  }, [gig, gpsToHomeMinutes, getReadyTime, showHomeRouting, isTrip])
}


// ════════════════════════════════════════════════════════════════
// PickupTimePicker — unchanged; no colour inline styles
// ════════════════════════════════════════════════════════════════
function PickupTimePicker({ value, onChange, autoFocus = false }) {
  const parse24 = (v) => {
    if (!v) return { display: '', period: 'AM' }
    const [hStr, mStr] = v.split(':')
    const h24 = parseInt(hStr, 10) || 0
    const m   = parseInt(mStr,  10) || 0
    const period = h24 >= 12 ? 'PM' : 'AM'
    const h12    = h24 % 12 || 12
    return { display: `${h12}:${String(m).padStart(2, '0')}`, period }
  }

  const parsed = parse24(value)
  const [displayVal, setDisplayVal] = React.useState(parsed.display)
  const [period,     setPeriod]     = React.useState(parsed.period)

  React.useEffect(() => {
    const p = parse24(value)
    setDisplayVal(p.display)
    setPeriod(p.period)
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const emit = (rawDisplay, rawPeriod) => {
    const match = rawDisplay.match(/^(\d{1,2}):(\d{2})$/)
    if (!match) return
    let h = parseInt(match[1], 10)
    const m = parseInt(match[2], 10)
    if (h < 1 || h > 12 || m < 0 || m > 59) return
    let h24 = h % 12
    if (rawPeriod === 'PM') h24 += 12
    onChange(`${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
  }

  const handleTextChange = (e) => {
    let v = e.target.value
    if (/^\d{3,4}$/.test(v.replace(':', ''))) {
      const digits = v.replace(':', '')
      v = digits.length === 3
        ? `${digits[0]}:${digits.slice(1)}`
        : `${digits.slice(0,2)}:${digits.slice(2)}`
    }
    setDisplayVal(v)
    emit(v, period)
  }

  const handlePeriodClick = (p) => {
    setPeriod(p)
    emit(displayVal, p)
  }

  return (
    <div className="tgc-pickup-row">
      <input
        type="text"
        inputMode="numeric"
        placeholder="9:43"
        value={displayVal}
        onChange={handleTextChange}
        autoFocus={autoFocus}
        aria-label="Pickup time"
        className="tgc-pickup-input"
      />
      <div className="tgc-pickup-period-group">
        {['AM', 'PM'].map(p => (
          <button
            key={p}
            type="button"
            onClick={() => handlePeriodClick(p)}
            className={`tgc-pickup-period-btn${period === p ? ' tgc-pickup-period-btn--active' : ''}`}
            aria-pressed={period === p}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  )
}


// ════════════════════════════════════════════════════════════════
// VenueToVenuePrompt
// ════════════════════════════════════════════════════════════════
function VenueToVenuePrompt({ currentGig, nextGig, onChoiceChange }) {
  const [choice,       setChoice]       = useState(null)
  const [routeState,   setRouteState]   = useState('idle')
  const [result,       setResult]       = useState(null)
  const [errorMsg,     setErrorMsg]     = useState('')
  const [urgencyClass, setUrgencyClass] = useState('tgc-v2v-leave-time--ok')

  useEffect(() => {
    if (!result?.leaveTime) return
    const calc = () => {
      const ld = parseShowTime(result.leaveTime)
      if (!ld) return
      const mins = Math.floor((ld - new Date()) / 60000)
      setUrgencyClass(
        mins <= 0  ? 'tgc-v2v-leave-time--overdue' :
        mins <= 30 ? 'tgc-v2v-leave-time--soon' :
        'tgc-v2v-leave-time--ok'
      )
    }
    calc()
    const id = setInterval(calc, 60000)
    return () => clearInterval(id)
  }, [result])

  const nextGigLabel = (nextGig.name || nextGig.venue_name || 'your next gig')
    + (nextGig.time ? ` at ${formatTimeStr(nextGig.time.slice(0, 5))}` : '')

  const fetchRoute = async () => {
    const fromAddress = currentGig.venue_address
    const toAddress   = nextGig.venue_address
    const toCallTime  = nextGig.call_time || nextGig.time
    if (!fromAddress || !toAddress) { setErrorMsg('One or both venues is missing an address.'); setRouteState('error'); return }
    if (!toCallTime) { setErrorMsg("Your next gig doesn't have a show time."); setRouteState('error'); return }
    setRouteState('loading')
    try {
      const res = await calculateVenueToVenueLeaveTime(fromAddress, toAddress, toCallTime.slice(0, 5))
      if (!res) { setErrorMsg("Couldn't calculate the route right now."); setRouteState('error'); return }
      setResult(res); setRouteState('result')
    } catch { setErrorMsg('Something went wrong while calculating the route.'); setRouteState('error') }
  }

  const selectChoice = (picked) => {
    if (picked === choice) return
    setChoice(picked)
    if (typeof onChoiceChange === 'function') onChoiceChange(picked)
    if (picked === 'v2v' && (routeState === 'idle' || routeState === 'error')) fetchRoute()
  }

  return (
    <div className="tgc-v2v-panel">
      <i className="bi bi-signpost-split-fill tgc-v2v-icon" />

      <div className="tgc-v2v-copy">
        <strong>Next up:</strong> {nextGigLabel}<br />
        Heading straight there, or going home first?
      </div>

      <div className="tgc-v2v-choice-row">
        <button
          className={`tgc-choice-btn ${choice === 'v2v' ? 'tgc-choice-btn--selected' : 'tgc-choice-btn--unselected'}`}
          onClick={() => selectChoice('v2v')}
        >
          <i className="bi bi-car-front-fill" /> Yes, route me
        </button>
        <button
          className={`tgc-choice-btn ${choice === 'home' ? 'tgc-choice-btn--selected' : 'tgc-choice-btn--unselected'}`}
          onClick={() => selectChoice('home')}
        >
          <i className="bi bi-house-fill" /> Going home first
        </button>
      </div>

      {/* Loading */}
      {choice === 'v2v' && routeState === 'loading' && (
        <div className="tgc-v2v-loading">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          Calculating drive time...
        </div>
      )}

      {/* Result */}
      {choice === 'v2v' && routeState === 'result' && result && (
        <div className="tgc-v2v-result">
          <div className="tgc-v2v-result-row">
            <i className="bi bi-car-front-fill tgc-v2v-result-icon" />
            <span>
              Leave <strong>{currentGig.venue_name || 'here'}</strong> by{' '}
              <strong className={`tgc-v2v-leave-time ${urgencyClass}`}>
                {formatTimeStr(result.leaveTime)}
              </strong>
            </span>
          </div>
          <div className="tgc-v2v-result__sub">
            {result.driveMinutes} min drive to {nextGig.name || nextGig.venue_name || 'next venue'} · 20 min buffer included
          </div>
        </div>
      )}

      {/* Error */}
      {choice === 'v2v' && routeState === 'error' && (
        <div className="tgc-v2v-error">
          <i className="bi bi-exclamation-triangle-fill tgc-v2v-error-icon" />
          {errorMsg}
          <button className="tgc-v2v-retry-btn" onClick={fetchRoute}>
            Retry
          </button>
        </div>
      )}

      {/* Home note */}
      {choice === 'home' && (
        <div className="tgc-v2v-home-note">
          Got it — your home leave time is shown on this card above.
        </div>
      )}
    </div>
  )
}


// ════════════════════════════════════════════════════════════════
// TodaysGigCard
// ════════════════════════════════════════════════════════════════
function TodaysGigCard({
  gig,
  musicTracks       = [],
  prepMinutes       = 90,
  nextGig           = null,
  prevGig           = null,
  prevGigV2vActive  = false,
  onV2vChoiceChange = null,
  homeLat           = null,
  homeLng           = null,
  gigDate           = null,
  hideEarnings      = false,
}) {

  const {
    currentTrack, isPlaying, progress,
    queue: queueRaw, queueIndex: queueIndexRaw, repeatMode: repeatModeRaw,
    setQueue, playTrack, togglePlay,
    nextTrack: nextTrackFn, prevTrack: prevTrackFn, cycleRepeat: cycleRepeatFn,
  } = useMusicPlayer()

  const queue       = Array.isArray(queueRaw) ? queueRaw : []
  const queueIndex  = typeof queueIndexRaw === 'number' ? queueIndexRaw : -1
  const repeatMode  = repeatModeRaw || 'none'
  const nextTrack   = typeof nextTrackFn   === 'function' ? nextTrackFn   : () => {}
  const prevTrack   = typeof prevTrackFn   === 'function' ? prevTrackFn   : () => {}
  const cycleRepeat = typeof cycleRepeatFn === 'function' ? cycleRepeatFn : () => {}

  // ── Trip mode ─────────────────────────────────────────────────
  const { activeTripToday, getActiveTripForDate } = useTripMode()
  const activeTrip       = gigDate ? getActiveTripForDate(gigDate) : activeTripToday
  const effectiveHomeLat = activeTrip ? activeTrip.stay_lat  : homeLat
  const effectiveHomeLng = activeTrip ? activeTrip.stay_lng  : homeLng

  const todayStr_tc = (() => {
    const n = new Date()
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`
  })()

  const isReturnDay = activeTrip &&
    activeTrip.depart_dest_at &&
    activeTrip.depart_dest_at.split('T')[0] === (gigDate || todayStr_tc)

  const transitBuffer  = activeTrip ? getTransitBufferMinutes(activeTrip.transport_mode) : 0
  const returnTerminal = isReturnDay && transitBuffer > 0 ? {
    label:    activeTrip.dest_terminal_label   || 'terminal',
    address:  activeTrip.dest_terminal_address || '',
    lat:      activeTrip.dest_terminal_lat,
    lng:      activeTrip.dest_terminal_lng,
    mode:     activeTrip.transport_mode,
    buffer:   transitBuffer,
    timezone: activeTrip.dest_timezone || null,
    departAt: activeTrip.depart_dest_at,
  } : null


  // ── Pickup time state ─────────────────────────────────────────
  const [localPickupTime, setLocalPickupTime] = useState(gig.pickup_time || '')
  const [editingPickup,   setEditingPickup]   = useState(false)
  const [pickupInput,     setPickupInput]     = useState(gig.pickup_time || '')
  const [pickupSaving,    setPickupSaving]    = useState(false)

  useEffect(() => {
    setLocalPickupTime(gig.pickup_time || '')
    setPickupInput(gig.pickup_time || '')
  }, [gig.pickup_time])

  const savePickupTime = async () => {
    setPickupSaving(true)
    const val = pickupInput.trim() || null
    try {
      const { error } = await supabase.from('gigs').update({ pickup_time: val }).eq('id', gig.id)
      if (!error) {
        setLocalPickupTime(val || '')
        setEditingPickup(false)
        await reschedulePickupNotification({
          gigId:         gig.id,
          gigDate:       gigDate || gig.date?.split('T')[0],
          newPickupTime: val,
          prepMinutes,
        })
      } else {
        console.error('[TodaysGigCard] pickup save error:', error)
      }
    } catch (err) { console.error('[TodaysGigCard] pickup save error:', err) }
    finally { setPickupSaving(false) }
  }

  const clearPickupTime = async () => {
    setPickupSaving(true)
    try {
      const { error } = await supabase.from('gigs').update({ pickup_time: null }).eq('id', gig.id)
      if (!error) {
        setLocalPickupTime(''); setPickupInput(''); setEditingPickup(false)
        await reschedulePickupNotification({
          gigId:         gig.id,
          gigDate:       gigDate || gig.date?.split('T')[0],
          newPickupTime: null,
          prepMinutes,
        })
      }
    } catch (err) { console.error('[TodaysGigCard] pickup clear error:', err) }
    finally { setPickupSaving(false) }
  }

  const pickupTimeParsed = localPickupTime
    ? parseShowTime(localPickupTime, gigDate || gig.date?.split('T')[0])
    : null


  // ── Music helpers ─────────────────────────────────────────────
  const setlistTracks = (gig?.setlist || []).map(entry => {
    const id = typeof entry === 'string' ? entry : entry?.id
    return musicTracks.find(t => t.id === id)
  }).filter(Boolean)

  function isCurrentTrack(track) { return currentTrack?.url === (track.audio_url || track.url) }

  function handleTrackClick(track, index) {
    const url = track.audio_url || track.url || ''
    if (isCurrentTrack(track)) { togglePlay(); return }
    if (typeof setQueue === 'function') {
      setQueue(setlistTracks.map(t => ({ name: t.title || t.name || 'Untitled', url: t.audio_url || t.url || '' })), index)
    } else {
      playTrack({ name: track.title || track.name || 'Untitled', url })
    }
  }


  // ── Core state ────────────────────────────────────────────────
  const [countdown,          setCountdown]          = useState(null)
  const [showTimeParsed,     setShowTimeParsed]     = useState(null)
  const [travelLegs,         setTravelLegs]         = useState([])
  const [travelStatus,       setTravelStatus]       = useState('idle')
  const [travelError,        setTravelError]        = useState(null)
  const [gpsToHomeMinutes,   setGpsToHomeMinutes]   = useState(null)
  const [prevGigMode,        setPrevGigMode]        = useState(null)

  const geocodedStayRef  = useRef({ address: null, lat: null, lon: null })
  const travelGenRef     = useRef(0)
  const hadActiveTripRef = useRef(!!activeTrip)

  useEffect(() => {
    if (prevGigV2vActive) setPrevGigMode('v2v')
    else if (!prevGig) { /* no prevGig */ }
    else setPrevGigMode(prev => prev === 'v2v' ? null : prev)
  }, [prevGigV2vActive, prevGig])

  useEffect(() => {
    setShowTimeParsed(parseShowTime(gig?.time, gigDate))
  }, [gig, gigDate])

  // Countdown tick
  useEffect(() => {
    if (!showTimeParsed) return
    const tick = () => {
      const diff = showTimeParsed - new Date()
      if (diff <= 0) { setCountdown({ hours: 0, minutes: 0, seconds: 0, started: true }); return }
      setCountdown({
        hours:   Math.floor(diff / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        started: false,
      })
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [showTimeParsed])


  // ── calculateTravel ──────────────────────────────────────────
  const calculateTravel = useCallback(async () => {
    if (!showTimeParsed) return

    const gen = ++travelGenRef.current

    setTravelStatus('routing'); setTravelError(null); setTravelLegs([])
    setGpsToHomeMinutes(null)

    let originLat = null, originLon = null, originLabel = 'Home'

    if (activeTrip?.stay_address) {
      const cached = geocodedStayRef.current
      if (cached.address === activeTrip.stay_address && cached.lat && cached.lon) {
        originLat = cached.lat; originLon = cached.lon
      } else {
        console.log('[TodaysGigCard] geocoding trip stay_address:', activeTrip.stay_address)
        try {
          const c = await geocodeAddress(activeTrip.stay_address)
          originLat = c.lat; originLon = c.lon
          geocodedStayRef.current = { address: activeTrip.stay_address, lat: c.lat, lon: c.lon }
        } catch (err) {
          console.warn('[TodaysGigCard] stay_address geocoding failed, falling back to stored coords:', err)
          originLat = parseFloat(effectiveHomeLat)
          originLon = parseFloat(effectiveHomeLng)
        }
      }
      originLabel = 'Trip Home'
    } else {
      originLat = parseFloat(effectiveHomeLat)
      originLon = parseFloat(effectiveHomeLng)
    }

    if (!originLat || !originLon || isNaN(originLat) || isNaN(originLon)) {
      if (travelGenRef.current !== gen) return
      setTravelStatus('error')
      setTravelError(activeTrip
        ? `Could not find your trip stay address: "${activeTrip.stay_address}".`
        : 'Set your home address in Settings to see leave times.')
      return
    }

    // GPS away-from-origin check (non-fatal)
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, {
          enableHighAccuracy: true, timeout: 10000, maximumAge: 0,
        })
      )
      const distKm = haversineKm(pos.coords.latitude, pos.coords.longitude, originLat, originLon)
      if (distKm > AWAY_FROM_HOME_THRESHOLD_KM) {
        try {
          const secs = await fetchDriveSeconds(pos.coords.longitude, pos.coords.latitude, originLon, originLat)
          if (travelGenRef.current === gen) setGpsToHomeMinutes(Math.ceil(secs / 60) + 5)
        } catch { /* skip */ }
      } else {
        if (travelGenRef.current === gen) setGpsToHomeMinutes(null)
      }
    } catch { /* GPS denied */ }

    // FIX 3: Always geocode venue fresh from address + city + state.
    let venueLat, venueLon
    if (!gig.venue_address) {
      if (travelGenRef.current !== gen) return
      setTravelStatus('error'); setTravelError('No venue address saved for this gig.'); return
    }
    try {
      const c = await geocodeAddress(gig.venue_address, gig.city || '', gig.state || '')
      venueLat = c.lat; venueLon = c.lon
    } catch (err) {
      if (travelGenRef.current !== gen) return
      setTravelStatus('error'); setTravelError(err.message || 'Could not find venue location.'); return
    }

    let errandLat = null, errandLon = null
    if (gig.errand_address?.trim()) {
      if (gig.errand_lat && gig.errand_lng) {
        errandLat = parseFloat(gig.errand_lat); errandLon = parseFloat(gig.errand_lng)
      } else {
        try { const c = await geocodeAddress(gig.errand_address); errandLat = c.lat; errandLon = c.lon } catch { /* skip */ }
      }
    }

    const getReadyLoc = gig.get_ready_location || 'home'
    let prepLat = null, prepLon = null
    if (getReadyLoc.startsWith('other') && gig.prep_address?.trim()) {
      if (gig.prep_lat && gig.prep_lng) {
        prepLat = parseFloat(gig.prep_lat); prepLon = parseFloat(gig.prep_lng)
      } else {
        try { const c = await geocodeAddress(gig.prep_address); prepLat = c.lat; prepLon = c.lon } catch { /* skip */ }
      }
    }

    const chain = [{ lat: originLat, lon: originLon, label: originLabel, type: 'home' }]
    if (errandLat !== null) chain.push({ lat: errandLat, lon: errandLon, label: gig.errand_address || 'Errand', type: 'errand', stayMinutes: parseInt(gig.errand_duration) || 30 })
    if (prepLat !== null) {
      const split = getReadyLoc.endsWith('_half') ? 0.5 : getReadyLoc.endsWith('_most') ? 0.7 : 1.0
      chain.push({ lat: prepLat, lon: prepLon, label: gig.prep_address || 'Getting ready location', type: 'prep', stayMinutes: Math.round(prepMinutes * split) })
    }
    chain.push({ lat: venueLat, lon: venueLon, label: gig.venue_name || 'Venue', type: 'venue', stayMinutes: 0 })

    const legDurations = []
    for (let i = 0; i < chain.length - 1; i++) {
      try {
        legDurations.push(await fetchDriveSeconds(chain[i].lon, chain[i].lat, chain[i+1].lon, chain[i+1].lat))
      } catch (err) {
        if (travelGenRef.current !== gen) return
        setTravelStatus('error'); setTravelError(err.message || `Could not calculate route to ${chain[i+1].label}.`); return
      }
    }

    const resolvedCallTime = gig.call_time
      ? parseShowTime(gig.call_time, gigDate || gig.date?.split('T')[0])
      : new Date(showTimeParsed.getTime() - 60 * 60 * 1000)

    let venueArrivalTarget
    if      (getReadyLoc === 'venue')                                     venueArrivalTarget = new Date(resolvedCallTime.getTime() - prepMinutes * 60 * 1000)
    else if (getReadyLoc === 'home_half' || getReadyLoc === 'other_half') venueArrivalTarget = new Date(resolvedCallTime.getTime() - Math.round(prepMinutes * 0.5) * 60 * 1000)
    else if (getReadyLoc === 'home_most' || getReadyLoc === 'other_most') venueArrivalTarget = new Date(resolvedCallTime.getTime() - Math.round(prepMinutes * 0.3) * 60 * 1000)
    else                                                                  venueArrivalTarget = resolvedCallTime

    const legs = []
    let currentArrivalTarget = venueArrivalTarget
    for (let i = chain.length - 2; i >= 0; i--) {
      const driveSec = legDurations[i]
      const leaveBy  = new Date(currentArrivalTarget.getTime() - (driveSec + TRAVEL_BUFFER_MINUTES * 60) * 1000)
      legs.unshift({ fromLabel: i === 0 ? originLabel : chain[i].label, fromType: chain[i].type, toLabel: chain[i+1].label, toType: chain[i+1].type, driveDurationSec: driveSec, leaveByTime: leaveBy })
      currentArrivalTarget = new Date(leaveBy.getTime() - (chain[i].stayMinutes || 0) * 60 * 1000)
    }

    if (travelGenRef.current !== gen) return
    setTravelLegs(legs); setTravelStatus('done')
  }, [gig, gigDate, showTimeParsed, prepMinutes, effectiveHomeLat, effectiveHomeLng, activeTrip])


  // ── Reset routing when effective home coords change ──────────
  const prevEffectiveCoords = useRef({ lat: null, lng: null })
  useEffect(() => {
    const prev = prevEffectiveCoords.current
    if ((prev.lat !== effectiveHomeLat || prev.lng !== effectiveHomeLng) && travelStatus !== 'idle') {
      travelGenRef.current++
      setTravelStatus('idle'); setTravelLegs([]); setTravelError(null); setGpsToHomeMinutes(null)
      geocodedStayRef.current = { address: null, lat: null, lon: null }
    }
    prevEffectiveCoords.current = { lat: effectiveHomeLat, lng: effectiveHomeLng }
  }, [effectiveHomeLat, effectiveHomeLng]) // eslint-disable-line react-hooks/exhaustive-deps

  const prevStayAddress = useRef(null)
  useEffect(() => {
    const addr = activeTrip?.stay_address ?? null
    if (addr !== prevStayAddress.current) {
      geocodedStayRef.current = { address: null, lat: null, lon: null }
      if (travelStatus !== 'idle') {
        travelGenRef.current++
        setTravelStatus('idle'); setTravelLegs([]); setTravelError(null); setGpsToHomeMinutes(null)
      }
      prevStayAddress.current = addr
    }
  }, [activeTrip?.stay_address]) // eslint-disable-line react-hooks/exhaustive-deps

  const activeTripId = activeTrip?.id ?? null
  useEffect(() => {
    const hasTrip = !!activeTripId
    if (hasTrip === hadActiveTripRef.current) return
    hadActiveTripRef.current = hasTrip
    if (!hasTrip) return
    geocodedStayRef.current = { address: null, lat: null, lon: null }
    travelGenRef.current++
    setTravelStatus('idle'); setTravelLegs([]); setTravelError(null); setGpsToHomeMinutes(null)
  }, [activeTripId])

  useEffect(() => {
    if (!showTimeParsed || travelStatus !== 'idle') return
    if (!prevGig || prevGigMode === 'home') calculateTravel()
  }, [showTimeParsed, prevGig, prevGigMode, travelStatus, calculateTravel])


  // ── Derived display values ────────────────────────────────────
  const getReadyLoc     = gig.get_ready_location || 'home'
  const firstLeg        = travelLegs[0] ?? null
  const hasPrepLeg      = travelLegs.some(l => l.toType === 'prep') && travelLegs.length > 1
  const prepArrivalLeg  = travelLegs.find(l => l.toType === 'prep') ?? null
  const venueArrivalLeg = travelLegs.find(l => l.toType === 'venue') ?? null
  const venueArrivalTime = venueArrivalLeg
    ? new Date(venueArrivalLeg.leaveByTime.getTime() + (venueArrivalLeg.driveDurationSec + TRAVEL_BUFFER_MINUTES * 60) * 1000)
    : null
  const prepArrivalTime = prepArrivalLeg
    ? new Date(prepArrivalLeg.leaveByTime.getTime() + (prepArrivalLeg.driveDurationSec + TRAVEL_BUFFER_MINUTES * 60) * 1000)
    : null

  // ── Get-ready time ────────────────────────────────────────────
  let getReadyTime = null, getReadyLabel = 'Get Ready', getReadySubLabel = ''
  // tgc-big-number variant class for the get-ready time colour
  let getReadyColorClass = 'tgc-big-number--getready'

  if (pickupTimeParsed && getReadyLoc !== 'venue') {
    getReadyTime      = new Date(pickupTimeParsed.getTime() - prepMinutes * 60 * 1000)
    getReadyLabel     = 'Start Getting Ready'
    getReadySubLabel  = `${prepMinutes} min before your ${formatTime(pickupTimeParsed)} pickup`
  } else if (getReadyLoc === 'home' && firstLeg) {
    getReadyTime      = new Date(firstLeg.leaveByTime.getTime() - prepMinutes * 60 * 1000)
    getReadyLabel     = 'Start Getting Ready'
    getReadySubLabel  = `${prepMinutes} min to get ready at home`
  } else if (getReadyLoc === 'venue' && venueArrivalTime) {
    getReadyTime        = venueArrivalTime
    getReadyLabel       = 'Arrive & Start Prep'
    getReadySubLabel    = `${prepMinutes} min of prep at the venue`
    getReadyColorClass  = 'tgc-big-number--venue'
  } else if (getReadyLoc === 'home_half' || getReadyLoc === 'other_half') {
    const halfPrep = Math.round(prepMinutes / 2)
    if (gig.prep_address && prepArrivalTime) {
      getReadyTime     = prepArrivalTime; getReadyLabel = 'Arrive & Start Prep'
      getReadySubLabel = `${halfPrep} min at ${gig.prep_address?.split(',')[0] || 'prep location'}, then ${halfPrep} min finishing at venue`
    } else if (firstLeg) {
      getReadyTime     = new Date(firstLeg.leaveByTime.getTime() - halfPrep * 60 * 1000)
      getReadyLabel    = 'Start Getting Ready'
      getReadySubLabel = `${halfPrep} min at home, then ${halfPrep} min finishing at venue`
    }
  } else if ((getReadyLoc === 'home_most' || getReadyLoc === 'other_most') && firstLeg) {
    const homePrep = Math.round(prepMinutes * 0.7), venuePrep = Math.round(prepMinutes * 0.3)
    getReadyTime     = new Date(firstLeg.leaveByTime.getTime() - homePrep * 60 * 1000)
    getReadyLabel    = 'Start Getting Ready'
    getReadySubLabel = `${homePrep} min at home (face & makeup), then ${venuePrep} min at venue (body, nails, contacts, facial hair, lashes)`
  } else if (getReadyLoc === 'other' && prepArrivalTime) {
    getReadyTime     = prepArrivalTime; getReadyLabel = 'Arrive & Get Ready'
    getReadySubLabel = `${prepMinutes} min at ${gig.prep_address?.split(',')[0] || 'your getting-ready location'}`
  }

  // ── Pickup urgency ────────────────────────────────────────────
  const latestPickupBy  = firstLeg?.leaveByTime ?? null
  const pickupMinsAway  = pickupTimeParsed ? Math.floor((pickupTimeParsed - new Date()) / 60000) : null
  const pickupIsOverdue = pickupMinsAway !== null && pickupMinsAway <= 0
  const pickupIsSoon    = pickupMinsAway !== null && pickupMinsAway > 0 && pickupMinsAway <= 20
  const pickupIsLate    = pickupTimeParsed && latestPickupBy ? pickupTimeParsed > latestPickupBy : false

  // Urgency color is now applied via tgc-big-number--overdue/soon/ok
  // classes at the render site instead of a computed inline value.

  const showVenuePrep    = !pickupTimeParsed && ['venue', 'home_half', 'other_half', 'home_most', 'other_most'].includes(getReadyLoc)
  const venuePrepMinutes = (getReadyLoc === 'home_half' || getReadyLoc === 'other_half')
    ? Math.round(prepMinutes / 2)
    : (getReadyLoc === 'home_most' || getReadyLoc === 'other_most') ? Math.round(prepMinutes * 0.3) : prepMinutes
  const venuePrepLabel   = (getReadyLoc === 'home_half' || getReadyLoc === 'other_half')
    ? 'Finish Prep at Venue'
    : (getReadyLoc === 'home_most' || getReadyLoc === 'other_most') ? 'Finish at Venue' : 'Full Prep at Venue'

  const callTime = gig.call_time
    ? parseShowTime(gig.call_time, gigDate || gig.date?.split('T')[0])
    : showTimeParsed ? new Date(showTimeParsed.getTime() - 60 * 60 * 1000) : null

  const minsUntilFirstLeave = firstLeg ? Math.floor((firstLeg.leaveByTime - new Date()) / 60000) : null
  const leaveUrgency = pickupTimeParsed
    ? (pickupIsOverdue ? 'overdue' : pickupIsSoon ? 'soon' : 'ok')
    : (minsUntilFirstLeave === null ? 'none' : minsUntilFirstLeave <= 0 ? 'overdue' : minsUntilFirstLeave <= 30 ? 'soon' : 'ok')

  const showHomeRouting = !prevGig || prevGigMode === 'home'

  useLeaveForHomeNotification({ gig, gpsToHomeMinutes, getReadyTime, showHomeRouting, isTrip: !!activeTrip })

  // GPS away-from-origin banner values
  const leaveCurrentLocationBy = (gpsToHomeMinutes !== null && getReadyTime)
    ? new Date(getReadyTime.getTime() - (gpsToHomeMinutes + HOME_ARRIVAL_BUFFER_MINUTES) * 60 * 1000)
    : null
  const minsUntilLeaveCurrent = leaveCurrentLocationBy ? Math.floor((leaveCurrentLocationBy - new Date()) / 60000) : null
  const leaveCurrentIsOverdue = minsUntilLeaveCurrent !== null && minsUntilLeaveCurrent <= 0
  const leaveCurrentIsSoon    = minsUntilLeaveCurrent !== null && minsUntilLeaveCurrent > 0 && minsUntilLeaveCurrent <= 20

  // Determine GPS banner modifier class
  const gpsBannerClass = leaveCurrentIsOverdue
    ? 'tgc-gps-banner tgc-gps-banner--overdue'
    : leaveCurrentIsSoon
    ? 'tgc-gps-banner tgc-gps-banner--warn'
    : 'tgc-gps-banner'

  // Icon color is now applied via tgc-gps-banner__icon--overdue/--warn
  // classes at the render site, driven directly by leaveCurrentIsOverdue.


  // ════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <div className="tgc-card">
      <div className="tgc-card__orb" aria-hidden="true" />

      {/* TODAY badge */}
      <div className="tgc-today-badge">
        <i className="bi bi-star-fill" /> Today
      </div>

      <h2 className="tgc-headline">
        {gig.name || gig.venue_name}
      </h2>

      {/* Venue address link */}
      <a href={buildMapsUrl(gig)} target="_blank" rel="noopener noreferrer" className="tgc-venue-link">
        <i className="bi bi-geo-alt-fill tgc-icon-gap" />
        {gig.venue_address || gig.venue_name}
        <i className="bi bi-box-arrow-up-right tgc-venue-link__ext" />
      </a>

      {/* ── Show / Call / Pay row ──────────────────────────────── */}
      <div className="tgc-time-row">
        <div>
          <p className="tgc-label">Show Time</p>
          <p className="tgc-time-value">
            {showTimeParsed ? formatTime(showTimeParsed) : gig.time}
          </p>
        </div>
        {callTime && (
          <div>
            <p className="tgc-label"><i className="bi bi-bell-fill tgc-icon-gap" />Call Time</p>
            <p className="tgc-call-time tgc-time-value tgc-time-value--noaccent">{formatTime(callTime)}</p>
            <p className="tgc-time-subcaption">
              {gig.call_time ? 'Set call time' : '1 hr before show (default)'}
            </p>
          </div>
        )}
        {!hideEarnings && (
          <div>
            <p className="tgc-label">Pay</p>
            <p className="tgc-time-value">${gig.payment}</p>
          </div>
        )}
      </div>

      {/* ── Trip mode banner ───────────────────────────────────── */}
      {activeTrip && (
        <div className="tgc-trip-banner">
          <div className="tgc-trip-banner__heading">
            <i className="bi bi-suitcase-fill" /> Traveling - {activeTrip.destination_label}
          </div>
          <div className="tgc-trip-banner__body">
            Routing calculates from your trip home address, not your address in Settings.
            If you go out while traveling, you'll see when you need to head back to get ready.
            {isReturnDay && transitBuffer > 0 && ` Return day: leave ${transitBuffer} min early for the ${activeTrip.transport_mode === 'flight' ? 'airport' : activeTrip.transport_mode === 'bus' ? 'bus terminal' : 'station'}.`}
          </div>
          {returnTerminal && (
            <div className="tgc-trip-banner__return">
              <i className={`bi bi-${returnTerminal.mode === 'flight' ? 'airplane-fill' : 'train-front-fill'} tgc-icon-gap`} />
              <strong>Return day:</strong> you {returnTerminal.mode === 'flight' ? 'fly' : 'depart'} at{' '}
              {(() => {
                const [, rawTime] = (returnTerminal.departAt || '').split('T')
                const [hh = '0', mm = '0'] = (rawTime || '').split(':')
                const d = new Date(2000, 0, 1, parseInt(hh, 10), parseInt(mm, 10))
                const ts = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                const tz = returnTerminal.timezone
                  ? new Intl.DateTimeFormat('en-US', { timeZone: returnTerminal.timezone, timeZoneName: 'short' })
                      .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || ''
                  : ''
                return tz ? `${ts} (${tz})` : ts
              })()} from {returnTerminal.label}. Leave {returnTerminal.buffer} min early to get there.
            </div>
          )}
        </div>
      )}

      {/* ── GPS away-from-origin banner ────────────────────────── */}
      {leaveCurrentLocationBy !== null && showHomeRouting && (
        <div className={gpsBannerClass}>
          <i className={`bi bi-geo-alt-fill tgc-gps-banner__icon ${leaveCurrentIsOverdue ? 'tgc-gps-banner__icon--overdue' : 'tgc-gps-banner__icon--warn'}`} />
          <span className="tgc-gps-banner__text">
            You're <strong>~{gpsToHomeMinutes >= 60
              ? `${Math.floor(gpsToHomeMinutes / 60)} hr ${gpsToHomeMinutes % 60} min`
              : `${gpsToHomeMinutes} min`
            } {activeTrip ? 'from your trip home' : 'from home'}.</strong>{' '}
            {leaveCurrentIsOverdue
              ? <>You needed to leave your current location{' '}
                  <strong className="tgc-text-overdue">already</strong>{' '}
                  to get {activeTrip ? 'back to your trip home' : 'home'}, have time to chill, and start getting ready.
                </>
              : <>Leave where you are by{' '}
                  <strong className={`tgc-gps-leave-time ${leaveCurrentIsSoon ? 'tgc-gps-leave-time--soon' : 'tgc-gps-leave-time--normal'}`}>
                    {formatTime(leaveCurrentLocationBy)}
                  </strong>{' '}
                  <span className="tgc-text-muted-inline">({minsUntilLeaveCurrent} min{leaveCurrentIsSoon ? ', leave soon!' : ''})</span>{' '}
                  to get {activeTrip ? 'back to your trip home' : 'home'}, have time to chill, and start getting ready.
                </>
            }
          </span>
        </div>
      )}

      {/* ── prevGig toggle ─────────────────────────────────────── */}
      {prevGig && !prevGigV2vActive && (
        <div className="tgc-prevgig-panel">
          <i className="bi bi-house-door-fill tgc-prevgig-icon" />
          <div className="tgc-prevgig-copy">
            <strong>Where are you coming from?</strong><br />
            Straight from <strong>{prevGig.name || prevGig.venue_name || 'your last show'}</strong>, or going home first?
          </div>
          <div className="tgc-v2v-choice-row">
            {[
              { mode: 'v2v',  icon: 'bi-signpost-split-fill', label: 'Straight from there' },
              { mode: 'home', icon: 'bi-house-fill',          label: 'Going home first'   },
            ].map(({ mode, icon, label }) => (
              <button key={mode}
                className={`tgc-choice-btn ${prevGigMode === mode ? 'tgc-choice-btn--selected' : 'tgc-choice-btn--unselected'}`}
                onClick={() => setPrevGigMode(prevGigMode === mode ? null : mode)}
              >
                <i className={`bi ${icon}`} /> {label}
              </button>
            ))}
          </div>
          {prevGigMode === 'v2v' && (
            <div className="tgc-prevgig-note">
              Check the card above for your venue-to-venue leave time.
            </div>
          )}
        </div>
      )}

      {/* ── Get-ready block ─────────────────────────────────────── */}
      {showHomeRouting && getReadyTime && !hasPrepLeg && (
        <div className="tgc-block tgc-block--ready">
          <div className="tgc-ready-info">
            <p className="tgc-label"><i className="bi bi-stars tgc-icon-gap" />{getReadyLabel}</p>
            <p className={`tgc-big-number ${getReadyColorClass}`}>
              {formatTime(getReadyTime)}
            </p>
            <p className="tgc-muted">
              {getReadySubLabel}
            </p>
          </div>
          {showVenuePrep && (
            <div className="tgc-venue-prep-badge">
              <p className="tgc-label tgc-label--flush"><i className="bi bi-building tgc-icon-gap" />{venuePrepLabel}</p>
              <p className="tgc-venue-prep-value">{venuePrepMinutes} min</p>
              {(getReadyLoc === 'home_most' || getReadyLoc === 'other_most') && (
                <p className="tgc-venue-prep-detail">body · nails · contacts · facial hair · lashes</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Countdown + Pickup/Leave grid ────────────────────────── */}
      <div className={`tgc-main-grid ${(hasPrepLeg || !showHomeRouting) ? 'tgc-main-grid--1col' : 'tgc-main-grid--2col'}`}>

        {/* Countdown */}
        <div className="tgc-block">
          <p className="tgc-label"><i className="bi bi-hourglass-split tgc-icon-gap" />Countdown</p>
          {!countdown
            ? <p className="tgc-big-number">--:--:--</p>
            : countdown.started
            ? <p className="tgc-big-number tgc-big-number--venue">Show started!</p>
            : <p className="tgc-big-number">
                {String(countdown.hours).padStart(2,'0')}<span className="tgc-countdown-sep">:</span>
                {String(countdown.minutes).padStart(2,'0')}<span className="tgc-countdown-sep">:</span>
                {String(countdown.seconds).padStart(2,'0')}
              </p>
          }
        </div>

        {/* PICKUP MODE: Pickup At */}
        {showHomeRouting && !hasPrepLeg && pickupTimeParsed && (
          <div className="tgc-block">
            <div className="tgc-block-header-row">
              <p className="tgc-label tgc-label--flush">
                <i className="bi bi-car-front-fill tgc-icon-gap" />Pickup At
              </p>
              {!editingPickup && (
                <button
                  className="tgc-edit-icon-btn"
                  onClick={() => { setPickupInput(localPickupTime); setEditingPickup(true) }}
                  title="Edit pickup time"
                >
                  <i className="bi bi-pencil-fill" />
                </button>
              )}
            </div>
            {editingPickup ? (
              <div className="tgc-inline-actions">
                <PickupTimePicker value={pickupInput} onChange={setPickupInput} autoFocus />
                <button onClick={savePickupTime} disabled={pickupSaving} className={`tgc-save-btn${pickupSaving ? ' tgc-save-btn--disabled' : ''}`}>
                  {pickupSaving ? '...' : 'Save'}
                </button>
                <button onClick={() => setEditingPickup(false)} className="tgc-cancel-btn">Cancel</button>
                <button onClick={clearPickupTime} disabled={pickupSaving} className={`tgc-clear-btn${pickupSaving ? ' tgc-clear-btn--disabled' : ''}`} title="Remove pickup time">
                  <i className="bi bi-x-circle" /> Remove
                </button>
              </div>
            ) : (
              <>
                <p className={`tgc-big-number tgc-big-number--${(pickupIsOverdue) ? 'overdue' : pickupIsSoon ? 'soon' : 'ok'}`}>{formatTime(pickupTimeParsed)}</p>
                {pickupIsOverdue && (
                  <p className="tgc-caption-overdue">
                    Pickup time has passed
                  </p>
                )}
                {pickupIsSoon && !pickupIsOverdue && (
                  <p className="tgc-caption-soon">
                    Pickup soon!
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* STANDARD MODE: Leave Home By */}
        {showHomeRouting && !hasPrepLeg && !pickupTimeParsed && (
          <div
            className={`tgc-block${travelStatus === 'error' ? ' tgc-block--retryable' : ''}`}
            onClick={travelStatus === 'error' ? calculateTravel : undefined}
            title={travelStatus === 'error' ? 'Tap to retry' : undefined}
          >
            <p className="tgc-label">
              <i className="bi bi-car-front-fill tgc-icon-gap" />
              {firstLeg ? `Leave ${firstLeg.fromLabel} By` : 'Leave Home By'}
              {firstLeg && (
                <span className="tgc-leg-arrow">
                   {firstLeg.toLabel}
                </span>
              )}
            </p>
            {(travelStatus === 'idle' || travelStatus === 'routing') && (
              <p className="tgc-status-line">
                {travelStatus === 'routing'
                  ? <><i className="bi bi-arrow-repeat tgc-spin-icon" />Routing...</>
                  : <span className="tgc-text-muted-inline">Waiting...</span>}
              </p>
            )}
            {travelStatus === 'error' && (
              <p className="tgc-error-line">
                <i className="bi bi-exclamation-triangle-fill tgc-icon-gap" />{travelError}
              </p>
            )}
            {travelStatus === 'done' && firstLeg && (
              <>
                <p className={`tgc-big-number tgc-big-number--${leaveUrgency === 'overdue' ? 'overdue' : leaveUrgency === 'soon' ? 'soon' : 'ok'}`}>
                  {formatTime(firstLeg.leaveByTime)}
                </p>
                <p className="tgc-muted">
                  {formatDuration(firstLeg.driveDurationSec)} drive + {TRAVEL_BUFFER_MINUTES} min buffer to arrive by call time
                </p>
                {leaveUrgency !== 'ok' && (
                  <p className={leaveUrgency === 'overdue' ? 'tgc-caption-overdue' : 'tgc-caption-soon'}>
                    {leaveUrgency === 'overdue' ? 'You should have left already!' : 'Leave soon!'}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* PICKUP MODE: Latest Pickup By */}
      {showHomeRouting && !hasPrepLeg && pickupTimeParsed && (
        <div className="tgc-block tgc-block--pickup">
          <p className="tgc-label tgc-label--flush">
            <i className="bi bi-clock-history tgc-icon-gap" />
            Latest Pickup By
            {firstLeg && (
              <span className="tgc-leg-arrow">
                 {firstLeg.toLabel}
              </span>
            )}
          </p>
          {(travelStatus === 'idle' || travelStatus === 'routing') && (
            <p className="tgc-muted">
              {travelStatus === 'routing' ? 'Calculating route...' : 'Waiting...'}
            </p>
          )}
          {travelStatus === 'error' && (
            <p className="tgc-error-line">
              <i className="bi bi-exclamation-triangle-fill tgc-icon-gap" />{travelError}
            </p>
          )}
          {travelStatus === 'done' && latestPickupBy && (
            <>
              <p className={`tgc-pickup-time-display${pickupIsLate ? ' tgc-pickup-time-display--late' : ''}`}>
                {formatTime(latestPickupBy)}
              </p>
              <p className="tgc-muted">
                {firstLeg && `${formatDuration(firstLeg.driveDurationSec)} drive + ${TRAVEL_BUFFER_MINUTES} min buffer`}
              </p>
              {pickupIsLate
                ? <p className="tgc-status-row tgc-status-row--warning">
                    <i className="bi bi-exclamation-triangle-fill" />
                    Your {formatTime(pickupTimeParsed)} pickup is after the safe deadline; you may miss call time.
                  </p>
                : <p className="tgc-status-row tgc-status-row--success">
                    <i className="bi bi-check-circle-fill" />
                    Your {formatTime(pickupTimeParsed)} pickup arrives before the deadline.
                  </p>
              }
            </>
          )}
        </div>
      )}

      {/* Set pickup time nudge */}
      {showHomeRouting && !pickupTimeParsed && !editingPickup && (
        <div className="tgc-mb-md">
          <button className="tgc-pickup-nudge" onClick={() => { setPickupInput(''); setEditingPickup(true) }}>
            <i className="bi bi-car-front-fill" /> Getting picked up? Set pickup time
          </button>
        </div>
      )}

      {/* Inline editor opened from nudge */}
      {showHomeRouting && !pickupTimeParsed && editingPickup && (
        <div className="tgc-block tgc-mb-md tgc-inline-actions">
          <i className="bi bi-car-front-fill tgc-inline-actions__icon" />
          <p className="tgc-label tgc-label--inline">Pickup Time</p>
          <PickupTimePicker value={pickupInput} onChange={setPickupInput} autoFocus />
          <button onClick={savePickupTime} disabled={pickupSaving || !pickupInput} className={`tgc-save-btn${(pickupSaving || !pickupInput) ? ' tgc-save-btn--disabled' : ''}`}>
            {pickupSaving ? '...' : 'Save'}
          </button>
          <button onClick={() => setEditingPickup(false)} className="tgc-cancel-btn">Cancel</button>
        </div>
      )}

      {/* ── Multi-leg route cards ─────────────────────────────── */}
      {showHomeRouting && travelStatus === 'done' && hasPrepLeg && travelLegs.length > 0 && (
        <div className="tgc-legs-wrap tgc-mb-md">
          {travelLegs.map((leg, i) => {
            const minsUntil = Math.floor((leg.leaveByTime - new Date()) / 60000)
            const urgency   = minsUntil <= 0 ? 'overdue' : minsUntil <= 30 ? 'soon' : 'ok'
            const icons     = { errand: 'bi-bag-fill', prep: 'bi-house-heart-fill', venue: 'bi-music-note-beamed' }
            const icon      = icons[leg.toType] || 'bi-arrow-right-circle-fill'
            return (
              <React.Fragment key={i}>
                {/* Insert get-ready block between legs 0 and 1 */}
                {i === 1 && getReadyTime && (
                  <div className="tgc-block tgc-block--ready">
                    <div className="tgc-ready-info">
                      <p className="tgc-label"><i className="bi bi-stars tgc-icon-gap" />{getReadyLabel}</p>
                      <p className={`tgc-big-number tgc-big-number--sm ${getReadyColorClass}`}>
                        {formatTime(getReadyTime)}
                      </p>
                      <p className="tgc-muted">{getReadySubLabel}</p>
                    </div>
                  </div>
                )}
                <div className="tgc-block tgc-block--leg">
                  <div className="tgc-leg-row">
                    <div className="tgc-leg-col">
                      <p className="tgc-label">
                        <i className={`bi ${icon} tgc-icon-gap`} />
                        Leave {leg.fromLabel} by
                        <span className="tgc-leg-arrow"> {leg.toLabel}</span>
                      </p>
                      <p className={`tgc-big-number tgc-big-number--sm tgc-big-number--${urgency}`}>
                        {formatTime(leg.leaveByTime)}
                      </p>
                      <p className="tgc-muted">
                        {formatDuration(leg.driveDurationSec)} drive + {TRAVEL_BUFFER_MINUTES} min buffer
                      </p>
                    </div>
                    {urgency !== 'ok' && (
                      <span className={`tgc-leg-urgency-tag tgc-leg-urgency-tag--${urgency}`}>
                        {urgency === 'overdue' ? '⚠ Late!' : '⏰ Soon!'}
                      </span>
                    )}
                  </div>
                </div>
              </React.Fragment>
            )
          })}
        </div>
      )}

      {/* ── Setlist player ─────────────────────────────────────── */}
      {setlistTracks.length > 0 && (
        <div className="tgc-block">
          <p className="tgc-label tgc-label--setlist">
            <i className="bi bi-music-note-list tgc-icon-gap" />
            Setlist: {setlistTracks.length} track{setlistTracks.length !== 1 ? 's' : ''}
          </p>
          <div className="tgc-tracklist">
            {setlistTracks.map((track, index) => {
              const isThisTrack   = isCurrentTrack(track)
              const isThisPlaying = isThisTrack && isPlaying
              return (
                <div key={track.id}>
                  <div className="tgc-track-row">
                    <button
                      onClick={() => handleTrackClick(track, index)}
                      aria-label={isThisPlaying ? `Pause ${track.title}` : `Play ${track.title}`}
                      className={`tgc-track-play-btn${isThisTrack ? ' tgc-track-play-btn--active' : ''}`}
                    >
                      <i className={isThisPlaying ? 'bi bi-pause-fill' : 'bi bi-play-fill'} />
                    </button>
                    <div className="tgc-track-info">
                      <p className={`tgc-track-title${isThisTrack ? ' tgc-track-title--active' : ''}`}>
                        {index + 1}. {track.title || track.name || 'Untitled Track'}
                      </p>
                      {track.artist && (
                        <p className="tgc-track-artist">{track.artist}</p>
                      )}
                    </div>
                    {track.duration_seconds && (
                      <p className="tgc-muted tgc-track-duration">
                        {Math.floor(track.duration_seconds / 60)}:{String(track.duration_seconds % 60).padStart(2, '0')}
                      </p>
                    )}
                  </div>
                  {isThisTrack && (
                    <div className="tgc-progress-track" style={{ '--tgc-progress': `${progress}%` }}>
                      <div className="tgc-progress-fill" />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {currentTrack && queue.length > 0 && (
            <div className="tgc-nowplaying-row">
              <div className="tgc-nowplaying-info">
                <p className="tgc-muted tgc-nowplaying-status">{isPlaying ? 'Now Playing' : 'Paused'}</p>
                <p className="tgc-nowplaying-name">{currentTrack.name}</p>
                {queue.length > 1 && <p className="tgc-queue-pos">{queueIndex + 1} of {queue.length}</p>}
              </div>
              <div className="tgc-nowplaying-controls">
                <button onClick={cycleRepeat} title={repeatMode === 'none' ? 'Repeat: Off' : repeatMode === 'all' ? 'Repeat All' : 'Repeat One'} className={`tgc-repeat-btn${repeatMode !== 'none' ? ' tgc-repeat-btn--active' : ''}`}>
                  <i className={repeatMode === 'one' ? 'bi bi-repeat-1' : 'bi bi-arrow-repeat'} />
                </button>
                <button onClick={prevTrack} disabled={queueIndex <= 0 && repeatMode !== 'all'} className={`tgc-skip-btn${(queueIndex <= 0 && repeatMode !== 'all') ? ' tgc-skip-btn--disabled' : ''}`}>
                  <i className="bi bi-skip-start-fill" />
                </button>
                <button onClick={togglePlay} className="tgc-nowplaying-play-btn">
                  <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`} />
                </button>
                <button onClick={nextTrack} disabled={queueIndex >= queue.length - 1 && repeatMode !== 'all'} className={`tgc-skip-btn${(queueIndex >= queue.length - 1 && repeatMode !== 'all') ? ' tgc-skip-btn--disabled' : ''}`}>
                  <i className="bi bi-skip-end-fill" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Venue-to-venue prompt ───────────────────────────────── */}
      {nextGig && <VenueToVenuePrompt currentGig={gig} nextGig={nextGig} onChoiceChange={onV2vChoiceChange} />}
    </div>
  )
}


// ════════════════════════════════════════════════════════════════
// TodaysGigs — multi-gig wrapper (called from dashboard.js)
// ════════════════════════════════════════════════════════════════
export function TodaysGigs({
  gigs         = [],
  musicTracks  = [],
  prepMinutes  = 90,
  homeLat      = null,
  homeLng      = null,
  gigDate      = null,
  hideEarnings = false,
}) {
  const [v2vChoices, setV2vChoices] = useState(() => new Array(gigs.length).fill(null))
  if (!gigs || gigs.length === 0) return null
  return (
    <div className="tgc-list-wrap">
      {gigs.map((gig, index) => (
        <div key={gig.id}>
          {gigs.length > 1 && (
            <div className="tgc-show-counter">
              Show {index + 1} of {gigs.length}
            </div>
          )}
          <TodaysGigCard
            gig={gig}
            musicTracks={musicTracks}
            prepMinutes={prepMinutes}
            nextGig={gigs[index + 1] || null}
            prevGig={gigs[index - 1] || null}
            prevGigV2vActive={index > 0 && v2vChoices[index - 1] === 'v2v'}
            homeLat={homeLat}
            homeLng={homeLng}
            gigDate={gigDate}
            hideEarnings={hideEarnings}
            onV2vChoiceChange={(picked) => {
              setV2vChoices(prev => {
                const next = [...prev]
                next[index] = picked
                return next
              })
            }}
          />
        </div>
      ))}
    </div>
  )
}

export default TodaysGigCard